/**
 * Concept art placeholder — deterministic per-concept visual used wherever a
 * creative concept has no rendered asset yet. Pure function of (concept, index)
 * so previews and list swatches stay identical.
 */

import React from 'react';
import type { CreativeConcept } from './types';

export const CONCEPT_PALETTE: Array<[string, string]> = [
  ['#f4c8b0', '#b5541f'],
  ['#bcd7f0', '#2b5d8f'],
  ['#cfe3c2', '#3f6b31'],
  ['#e8d3ea', '#7a4a85'],
  ['#f0e6b8', '#8a7420'],
  ['#d9c8f0', '#5c3f8a'],
];

export function conceptAssetUrl(concept: CreativeConcept): string | undefined {
  // The picked take is the master image for all renditions and previews.
  const selected = concept.variations?.find((v) => v.id === concept.selectedVariation);
  return (
    selected?.assetUrl ??
    concept.variations?.find((v) => v.assetUrl)?.assetUrl ??
    concept.renditions?.find((r) => r.assetUrl)?.assetUrl
  );
}

/**
 * The image a concept actually SHOWS, including the conversation's uploaded images
 * as the positional fallback the previews use (see CreativePreviews' fallbackUrl).
 *
 * Publishing must resolve a concept's master image through exactly this function,
 * not its own narrower lookup. It previously read only
 * `variations[selectedVariation] ?? variations[0]`.assetUrl, which misses three cases
 * the canvas displays happily: a selected variation that carries no asset while a
 * later one does, an asset living only on a rendition, and an uploaded image standing
 * in for a concept with no generated asset. Any of those produced an ad with NO image
 * while every visualizer showed one — Meta then rejects the creative, and for a
 * lead-form ad it does so with a confusing "link field is required".
 */
export function conceptPublishAssetUrl(
  concept: CreativeConcept,
  index: number,
  uploadedImages?: Array<{ url: string }>,
): string | undefined {
  const own = conceptAssetUrl(concept);
  if (own) return own;
  if (!uploadedImages?.length) return undefined;
  return uploadedImages[index % uploadedImages.length]?.url;
}

/** Fills its parent — wrap in a sized, overflow-hidden container. `objectPosition`
 *  (e.g. "40% 30%") shifts the cover-crop toward a saved focal point so the small
 *  preview approximates the crop set in the full preview modal / published at that
 *  format. */
export const ConceptArt: React.FC<{ concept: CreativeConcept; index: number; fallbackUrl?: string; objectPosition?: string; format?: string }> = ({
  concept,
  index,
  fallbackUrl,
  objectPosition,
  format,
}) => {
  // A finished ad (words in the image) is shown whole, never cropped, using that format's own file.
  const finished = concept.bakedCopy === true;
  const sized = finished && format
    ? concept.renditions?.find((r) => r.format === format && r.assetUrl)?.assetUrl
    : undefined;
  const url = sized ?? conceptAssetUrl(concept) ?? fallbackUrl;
  if (url) {
    return (
      <img
        src={url}
        alt={concept.concept ?? concept.id}
        className={finished ? 'h-full w-full object-contain bg-inset' : 'h-full w-full object-cover'}
        style={objectPosition && !finished ? { objectPosition } : undefined}
      />
    );
  }
  const [bg, fg] = CONCEPT_PALETTE[index % CONCEPT_PALETTE.length];
  return (
    <div className="flex h-full w-full items-center justify-center" style={{ backgroundColor: bg }}>
      <svg viewBox="0 0 100 100" className="h-[70%] w-[70%]">
        <circle cx={30 + (index % 4) * 10} cy="40" r="18" fill={fg} opacity="0.85" />
        <rect x="20" y="66" width="60" height="7" rx="3" fill={fg} opacity="0.6" />
        <rect x="30" y="78" width="40" height="5" rx="2" fill={fg} opacity="0.35" />
      </svg>
    </div>
  );
};
